/**
 * RejoinPrompt — surfaces a "click anywhere to resume" banner when
 * apex-voice has signalled an in-flight rejoin (server is dialing the
 * rep back into a conference) but the Twilio Voice SDK is still gated
 * by the browser autoplay policy.
 *
 * The browser blocks the SDK's AudioContext until any user gesture has
 * occurred on the page. After a refresh-during-call, the rep has no
 * primaryCall and no active-call modal — only this banner tells them
 * they need to click. The first click anywhere on the document fires
 * `initWebRtcOnGesture`'s handler (registered in softphone.ts), which
 * dismisses this banner immediately and kicks off the SDK init.
 *
 * The banner is purely informational — it has no buttons. Clicking the
 * banner itself counts as a gesture (clicks bubble to document) so it
 * dismisses on contact like everywhere else.
 */
import { useEffect, useState } from 'react';
import { PhoneIncoming } from 'lucide-react';

export function RejoinPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { visible?: boolean } | undefined;
      setVisible(detail?.visible === true);
    };
    window.addEventListener('apex:rejoin-prompt', handler);
    return () => window.removeEventListener('apex:rejoin-prompt', handler);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[10000] flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 text-white shadow-lg ring-1 ring-amber-600/40 animate-pulse"
    >
      <PhoneIncoming className="w-4 h-4" />
      <span className="text-sm font-medium">Click anywhere to resume your call</span>
    </div>
  );
}
